import { useEffect, useState } from 'react';
import { useAuthStore } from '@/stores/useAuthStore';
import { runPendingMigrations, DATA_MIGRATIONS } from '@/services/dataMigrationService';
import { notifyNewFeature, requestNotificationPermission } from '@/services/featureNotificationService';
import { toast } from 'sonner';

interface MigrationState {
  isRunning: boolean;
  isComplete: boolean;
  hasErrors: boolean;
  total: number;
  successful: number;
  failed: number;
  errors: string[];
}

/**
 * Hook para executar migrações de dados automaticamente no login
 * 
 * Executa em background e não bloqueia a aplicação.
 * Se houver falhas, apenas loga os erros sem travar o app.
 */
export function useDataMigration() {
  const { user } = useAuthStore();
  const [state, setState] = useState<MigrationState>({
    isRunning: false,
    isComplete: false,
    hasErrors: false,
    total: 0,
    successful: 0,
    failed: 0,
    errors: []
  });

  useEffect(() => {
    if (!user?.id) return;

    const runMigrations = async () => {
      setState(prev => ({ ...prev, isRunning: true }));

      try {
        const results = await runPendingMigrations(user.id);

        setState({
          isRunning: false,
          isComplete: true,
          hasErrors: results.failed > 0,
          total: results.total,
          successful: results.successful,
          failed: results.failed,
          errors: results.errors
        });

        // Notificar usuário se houve migrações bem-sucedidas
        if (results.total > 0 && results.successful > 0) {
          console.log(`✅ ${results.successful} migração(ões) executada(s) com sucesso`);
          
          // Verificar se alguma migração trouxe uma nova feature
          const migrationsWithFeatures = DATA_MIGRATIONS.filter(migration => {
            return migration.feature && results.successful > 0;
          });
          
          // Enviar notificações para cada nova feature
          if (migrationsWithFeatures.length > 0) {
            // Solicitar permissão de notificação (se ainda não foi concedida)
            const hasPermission = await requestNotificationPermission();
            
            if (hasPermission) {
              // Aguardar um pouco para não sobrepor com outras notificações
              await new Promise(resolve => setTimeout(resolve, 2000));
              
              for (const migration of migrationsWithFeatures) {
                if (migration.feature) {
                  await notifyNewFeature({
                    name: migration.feature.name,
                    description: migration.feature.description,
                    icon: migration.feature.icon,
                    migrationId: migration.id
                  });
                  
                  // Aguardar um pouco entre notificações
                  await new Promise(resolve => setTimeout(resolve, 1000));
                }
              }
              
              console.log(`🎉 ${migrationsWithFeatures.length} notificação(ões) de feature enviada(s)`);
            } else {
              console.log('⚠️ Usuário não concedeu permissão para notificações');
            }
          }
          
          if (results.failed > 0) {
            console.warn(
              `⚠️ Migrações: ${results.successful} sucesso, ${results.failed} falhas`
            );
          }
        }
      } catch (error) {
        console.error('❌ Erro ao executar migrações:', error);
        setState(prev => ({
          ...prev,
          isRunning: false,
          isComplete: true,
          hasErrors: true,
          errors: [error instanceof Error ? error.message : String(error)]
        }));
      }
    };

    // Executar com delay pequeno para não competir com carregamento inicial
    const timer = setTimeout(runMigrations, 2000);
    return () => clearTimeout(timer);
  }, [user?.id]);

  return state;
}
